import type {
  ImageLink,
  ResponsiveImage1,
  ResponsiveImage2,
  ResponsiveBodyImage,
  ResponsiveBodyImages,
} from '../../../amplienceTypes/partials/exported/responsive-image-schema';
import type { FC, ReactElement } from 'react';

import { Fragment } from 'react';

export type Source = {
  src: ImageLink | undefined;
  params?: string;
  minWidth: number;
  widths: number[];
  pixelDensity?: number;
};

type CustomSources = {
  customSources?: Source[];
};

type LayoutAwareProps = {
  imageSizes?: string;
};

const ImageSource: FC<ResponsiveImage1 & ResponsiveImage2 & CustomSources & LayoutAwareProps> = ({
  images,
  desktop,
  tablet,
  mobile,
  customSources,
  imageSizes,
}) => {
  const desktopSrc = desktop || images?.desktop;
  const tabletSrc = tablet || images?.tablet;
  const mobileSrc = mobile || images?.mobile;

  const sources = [
    {
      src: desktopSrc,
      params: images?.desktopParams,
      minWidth: 1680,
      widths: [1600, 1920, 2400, 2560, 3200, 3840, 4096, 5120],
    },
    {
      src: desktopSrc,
      params: images?.desktopParams,
      minWidth: 1024,
      widths: [800, 1000, 1200, 1400, 1600, 1920, 2048, 2400, 2880, 3072],
    },
    {
      src: tabletSrc,
      params: images?.tabletParams,
      minWidth: 768,
      widths: [600, 800, 1000, 1200, 1536, 1668, 2048],
    },
    {
      src: mobileSrc,
      params: images?.mobileParams,
      minWidth: 0,
      widths: [400, 500, 640, 750, 828, 1080, 1200, 1350],
    },
    ...(customSources || []),
  ].sort((a, b) => b.minWidth - a.minWidth);

  return (
    <>
      {sources.map((source, i) =>
        source.src ? (
          <source
            key={i}
            media={`(min-width: ${source.minWidth}px)`}
            sizes={imageSizes}
            srcSet={source.widths
              .map(w => {
                const sourceParams = source.params ?? `fmt=auto&w=${w}&upscale=false`;

                return `https://${source?.src?.defaultHost}/i/${source?.src?.endpoint}/${encodeURI(
                  source?.src?.name ?? ''
                )}?${sourceParams} ${w}w`;
              })
              .join(', ')}
          />
        ) : (
          <Fragment key={i} />
        )
      )}
    </>
  );
};

const BodyImageSource = ({
  desktop,
  tablet,
  mobile,
  images,
}: ResponsiveBodyImage & ResponsiveBodyImages): ReactElement => {
  const sources = [
    {
      src: desktop || images?.desktop,
      minWidth: 1024,
    },
    {
      src: tablet || images?.tablet,
      minWidth: 768,
    },
    {
      src: mobile || images?.mobile,
      minWidth: 0,
    },
  ];

  return (
    <>
      {sources.map((source, i) =>
        source.src ? (
          <source
            key={i}
            media={`(min-width: ${source.minWidth}px)`}
            srcSet={`
            //${source.src.image?.defaultHost}/i/${source.src.image?.endpoint}/${encodeURI(
              source.src.image?.name ?? ''
            )}${
              source.src.paddingLeft || source.src.paddingRight
                ? `?$padding$&gs_pl=${source.src.paddingLeft ?? 0}&gs_pr=${
                    source.src.paddingRight ?? 0
                  }&gs_src=${encodeURI(source.src?.image?.name ?? '')}`
                : ''
            }`}
          />
        ) : (
          <Fragment key={i} />
        )
      )}
    </>
  );
};

export { ImageSource, BodyImageSource };
